import { useMemo, useState } from 'react';
import {
  CurrencyCode,
  fromMinorUnits,
  formatMoney,
  sumMinor,
  toMinorUnits,
  type Invoice,
  type Payment,
} from '@lombok/shared';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@lombok/ui';
import { StatusPill } from '@/components/StatusPill';
import { FormError } from '@/components/form';
import { useAllocatePayment, useInvoices } from '@/data/hooks';
import { OPEN_INVOICE_STATUSES, invoiceNumberLabel } from '@/features/invoices/labels';
import { formatDecimalInput, parseDecimal } from '@/lib/decimal';

interface PaymentAllocatePanelProps {
  payment: Payment;
}

/**
 * "Asignar a facturas" — finance/admin applies an APPROVED customer payment to
 * the account's open invoices. We fetch the account's open invoices (emitida /
 * parcial / vencida), default each row to min(remaining payment, invoice saldo),
 * and submit the non-zero rows via {@link useAllocatePayment}. Σ is validated
 * client-side (≤ payment amount); the backend re-checks the cap and each invoice
 * balance and is the sole authority.
 *
 * Allocation amounts are decimals in the payment's currency and the backend
 * reconciles them against each invoice's USD balance. In MVP this only lines up
 * cleanly for USD payments, so the panel is offered for USD payments only — a
 * VES payment shows a short note instead of risky cross-currency math.
 */
export function PaymentAllocatePanel({ payment }: PaymentAllocatePanelProps) {
  const isUsd = payment.currency === CurrencyCode.USD;

  // One query per open status, merged. The backend scopes to this customer too.
  const issued = useInvoices(
    { customerId: payment.accountId, status: 'issued', limit: 100 },
    isUsd,
  );
  const partial = useInvoices(
    { customerId: payment.accountId, status: 'partially_paid', limit: 100 },
    isUsd,
  );
  const overdue = useInvoices(
    { customerId: payment.accountId, status: 'overdue', limit: 100 },
    isUsd,
  );

  const loading = issued.isLoading || partial.isLoading || overdue.isLoading;
  const loadError = issued.error ?? partial.error ?? overdue.error;

  const invoices = useMemo(() => {
    const all = [
      ...(issued.data?.items ?? []),
      ...(partial.data?.items ?? []),
      ...(overdue.data?.items ?? []),
    ].filter((inv) => OPEN_INVOICE_STATUSES.includes(inv.status) && inv.balanceMinorUsd > 0);
    // De-dup (a status overlap shouldn't happen, but be safe) and show oldest-due first.
    const byId = new Map(all.map((inv) => [inv.invoiceId, inv]));
    return [...byId.values()].sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  }, [issued.data, partial.data, overdue.data]);

  if (!isUsd) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Asignar a facturas</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-sm text-text-muted">
            La asignación a facturas está disponible para pagos en USD. Este pago fue registrado
            en {payment.currency}.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Asignar a facturas</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <p className="py-2 text-sm text-text-muted">Cargando facturas abiertas…</p>
        ) : loadError ? (
          <p className="py-2 text-sm text-danger">
            No se pudieron cargar las facturas abiertas de la cuenta.
          </p>
        ) : invoices.length === 0 ? (
          <p className="py-2 text-sm text-text-muted">
            La cuenta no tiene facturas abiertas para asignar este pago.
          </p>
        ) : (
          <AllocateForm payment={payment} invoices={invoices} />
        )}
      </CardContent>
    </Card>
  );
}

/** The editable allocation rows + submit, shown once open invoices are loaded. */
function AllocateForm({ payment, invoices }: { payment: Payment; invoices: Invoice[] }) {
  const allocate = useAllocatePayment(payment.paymentId, payment.accountId);

  // Amounts are USD minor units internally for integer-safe math; the wire body
  // sends decimals (toMinorUnits is reapplied by the backend in the same currency).
  const paymentMinor = payment.amountMinor;

  // Default: greedily fill each invoice's saldo up to the remaining payment.
  const [amountsMinor, setAmountsMinor] = useState<Record<string, number>>(() => {
    let remaining = paymentMinor;
    const next: Record<string, number> = {};
    for (const inv of invoices) {
      const apply = Math.max(0, Math.min(remaining, inv.balanceMinorUsd));
      next[inv.invoiceId] = apply;
      remaining -= apply;
    }
    return next;
  });
  // What each box shows while typing ("884,75" with the decimal comma); `amountsMinor` holds the
  // parsed, clamped value the totals and the request use.
  const [texts, setTexts] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      Object.entries(amountsMinor).map(([id, minor]) => [id, formatDecimalInput(fromMinorUnits(minor, CurrencyCode.USD))]),
    ),
  );

  const totalMinor = sumMinor(Object.values(amountsMinor));
  const remainingMinor = paymentMinor - totalMinor;
  const overAllocated = totalMinor > paymentMinor;
  const nothingAllocated = totalMinor <= 0;

  function setAmount(invoiceId: string, raw: string, balanceMinor: number) {
    setTexts((prev) => ({ ...prev, [invoiceId]: raw }));
    const parsed = parseDecimal(raw);
    if (raw.trim() === '' || Number.isNaN(parsed) || parsed < 0) {
      setAmountsMinor((prev) => ({ ...prev, [invoiceId]: 0 }));
      return;
    }
    // Clamp to the invoice's saldo (the backend rejects over-balance allocations anyway).
    const minor = Math.min(toMinorUnits(parsed, CurrencyCode.USD), balanceMinor);
    setAmountsMinor((prev) => ({ ...prev, [invoiceId]: minor }));
  }

  function submit() {
    const allocations = invoices
      .map((inv) => ({ invoiceId: inv.invoiceId, amount: fromMinorUnits(amountsMinor[inv.invoiceId] ?? 0, CurrencyCode.USD) }))
      .filter((a) => a.amount > 0);
    if (allocations.length === 0) return;
    allocate.mutate({ allocations });
  }

  const succeeded = allocate.isSuccess;

  return (
    <div className="flex flex-col gap-4">
      <FormError error={allocate.error} />

      {succeeded ? (
        <div className="rounded-md border border-success-soft bg-success-soft/50 p-3 text-sm text-text-primary">
          Pago asignado correctamente. Se aplicaron{' '}
          <span className="font-semibold">{formatMoney(allocate.data.allocated, CurrencyCode.USD)}</span>{' '}
          a {allocate.data.invoiceIds.length} factura
          {allocate.data.invoiceIds.length === 1 ? '' : 's'}.
        </div>
      ) : null}

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-border text-xs uppercase tracking-wide text-text-muted">
              <th className="px-3 py-2 font-medium">Factura</th>
              <th className="px-3 py-2 font-medium">Estado</th>
              <th className="px-3 py-2 text-right font-medium">Saldo</th>
              <th className="px-3 py-2 text-right font-medium">Asignar (USD)</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.invoiceId} className="border-b border-border last:border-0">
                <td className="px-3 py-2 font-mono text-brand-primary">{invoiceNumberLabel(inv)}</td>
                <td className="px-3 py-2">
                  <StatusPill kind="invoice" value={inv.status} />
                </td>
                <td className="px-3 py-2 text-right text-text-muted">
                  {formatMoney(inv.balanceMinorUsd, CurrencyCode.USD)}
                </td>
                <td className="px-3 py-2 text-right">
                  <input
                    type="text"
                    inputMode="decimal"
                    disabled={succeeded || allocate.isPending}
                    value={texts[inv.invoiceId] ?? ''}
                    onChange={(e) => setAmount(inv.invoiceId, e.target.value, inv.balanceMinorUsd)}
                    // On leaving the box, show the amount that was actually kept (clamped to the saldo).
                    onBlur={() =>
                      setTexts((prev) => ({
                        ...prev,
                        [inv.invoiceId]: formatDecimalInput(
                          fromMinorUnits(amountsMinor[inv.invoiceId] ?? 0, CurrencyCode.USD),
                        ),
                      }))
                    }
                    aria-label={`Monto a asignar a la factura ${invoiceNumberLabel(inv)}`}
                    className="h-10 w-32 rounded-md border border-border-strong bg-surface px-3 text-right text-sm text-text-primary focus-visible:border-brand-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Running totals against the payment amount. */}
      <dl className="flex flex-col gap-1 text-sm">
        <div className="flex items-center justify-between">
          <dt className="text-text-muted">Monto del pago</dt>
          <dd className="font-medium text-text-primary">
            {formatMoney(paymentMinor, CurrencyCode.USD)}
          </dd>
        </div>
        <div className="flex items-center justify-between">
          <dt className="text-text-muted">Total a asignar</dt>
          <dd className={`font-semibold ${overAllocated ? 'text-danger' : 'text-text-primary'}`}>
            {formatMoney(totalMinor, CurrencyCode.USD)}
          </dd>
        </div>
        <div className="flex items-center justify-between">
          <dt className="text-text-muted">Sin asignar</dt>
          <dd className="text-text-muted">
            {formatMoney(Math.max(0, remainingMinor), CurrencyCode.USD)}
          </dd>
        </div>
      </dl>

      {overAllocated ? (
        <p className="text-xs text-danger" role="alert">
          La suma asignada excede el monto del pago.
        </p>
      ) : null}

      <div className="flex justify-end">
        <Button
          onClick={submit}
          disabled={allocate.isPending || succeeded || overAllocated || nothingAllocated}
        >
          {allocate.isPending ? 'Asignando…' : 'Asignar pago'}
        </Button>
      </div>
    </div>
  );
}
